import type {
  ConnectionEvent,
  ConnectionIdea,
  FamilyOverviewRow,
  Household,
  Member,
  PendingApproval,
  SentBack,
  Plan,
  SessionUser,
  Task,
  TodayView,
  WeekView,
} from '../../shared/types'

export class ApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  })

  if (!response.ok) {
    let message = `Request failed (${response.status})`
    try {
      const body = (await response.json()) as { error?: string }
      if (body.error) message = body.error
    } catch {
      // Non-JSON error body; the status-based message stands.
    }
    throw new ApiError(response.status, message)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) })

const patch = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) })

const put = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) })

const del = <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' })

/** A member as shown before anyone has signed in: no limits, no settings. */
export interface PublicMember {
  id: string
  name: string
  role: 'parent' | 'kid'
  emoji: string
  color: string
}

export type Bootstrap =
  | { state: 'anonymous'; session: null }
  | {
      state: 'signed_in'
      household: Household | null
      members: Member[]
      session: SessionUser & { hasAccount: boolean }
    }

export interface SignUpInput {
  email: string
  password: string
  householdName: string
  timezone: string
  pin: string
  members: { name: string; role: 'parent' | 'kid'; emoji: string }[]
  meIndex: number
}

export interface SignInResult {
  ok: true
  needsChoice: boolean
  households?: { memberId: string; householdName: string; name: string; emoji: string }[]
}

export interface JoinResult {
  ok: true
  householdName: string
  role: 'kid' | 'parent'
  members: PublicMember[]
}

export interface Invite {
  code: string
  role: 'kid' | 'parent'
  memberId: string | null
  expiresAt: string
  maxUses: number
  uses: number
}

export interface LinkedDeviceInfo {
  id: string
  label: string | null
  createdAt: string
  lastSeenAt: string | null
  isThisDevice: boolean
}

export interface ConnectionView {
  weekKey: string
  ideas: ConnectionIdea[]
  events: ConnectionEvent[]
  metByMember: Record<string, boolean>
}

const memberQuery = (memberId?: string): string => (memberId ? `?memberId=${encodeURIComponent(memberId)}` : '')

export const api = {
  bootstrap: (): Promise<Bootstrap> => request('/bootstrap'),

  signUp: (input: SignUpInput): Promise<{ ok: true; householdId: string }> => post('/auth/signup', input),
  /** `identifier` is a parent's email or a kid's username. */
  signIn: (identifier: string, password: string): Promise<SignInResult> =>
    post('/auth/signin', { identifier, password }),
  chooseHousehold: (email: string, password: string, memberId: string): Promise<{ ok: true }> =>
    post('/auth/choose-household', { email, password, memberId }),
  join: (code: string): Promise<JoinResult> => post('/auth/join', { code }),
  claimParentProfile: (input: {
    memberId: string
    email: string
    password: string
    pin: string
  }): Promise<{ ok: true }> => post('/auth/claim', input),
  forgotPassword: (email: string): Promise<{ ok: true; emailConfigured: boolean; message: string }> =>
    post('/auth/forgot', { email }),
  resetPassword: (token: string, password: string): Promise<{ ok: true }> =>
    post('/auth/reset', { token, password }),
  changePassword: (currentPassword: string, newPassword: string): Promise<{ ok: true }> =>
    post('/auth/change-password', { currentPassword, newPassword }),

  logout: (): Promise<{ ok: true }> => del('/session'),

  invites: (): Promise<Invite[]> => request('/invites'),
  createInvite: (input: {
    role?: 'kid' | 'parent'
    memberId?: string | null
    hours?: number
    maxUses?: number
  }): Promise<{ code: string; expiresAt: string }> => post('/invites', input),
  revokeInvite: (code: string): Promise<{ ok: true }> => del(`/invites/${code}`),

  devices: (): Promise<LinkedDeviceInfo[]> => request('/devices'),
  revokeDevice: (id: string): Promise<{ ok: true }> => del(`/devices/${id}`),

  today: (memberId?: string): Promise<TodayView> => request(`/today${memberQuery(memberId)}`),
  week: (memberId?: string): Promise<WeekView> => request(`/week${memberQuery(memberId)}`),
  family: (): Promise<FamilyOverviewRow[]> => request('/family'),

  tasks: (): Promise<Task[]> => request('/tasks'),
  createTask: (input: Partial<Task> & { title: string }): Promise<Task> => post('/tasks', input),
  updateTask: (id: string, input: Partial<Task>): Promise<Task> => patch(`/tasks/${id}`, input),
  deleteTask: (id: string): Promise<{ ok: true }> => del(`/tasks/${id}`),

  complete: (taskId: string, memberId?: string, note?: string): Promise<{ ok: true; periodKey: string }> =>
    post('/completions', { taskId, memberId, note }),
  uncomplete: (taskId: string, memberId?: string, periodKey?: string): Promise<{ ok: true }> => {
    const params = new URLSearchParams()
    if (memberId) params.set('memberId', memberId)
    if (periodKey) params.set('periodKey', periodKey)
    const query = params.toString()
    return del(`/completions/${taskId}${query ? `?${query}` : ''}`)
  },

  approvals: (): Promise<PendingApproval[]> => request('/approvals'),
  approve: (completionId: string): Promise<{ ok: true }> => post(`/completions/${completionId}/approve`, {}),
  sendBack: (completionId: string): Promise<{ ok: true }> => post(`/completions/${completionId}/send-back`, {}),
  sentBack: (): Promise<SentBack[]> => request('/send-backs'),

  plans: (scope?: 'all'): Promise<Plan[]> => request(`/plans${scope ? `?scope=${scope}` : ''}`),
  createPlan: (input: {
    title: string
    details?: string | null
    kind?: Plan['kind']
    startsAt: string
    endsAt?: string | null
  }): Promise<Plan> => post('/plans', input),
  decidePlan: (id: string, status: 'approved' | 'declined', note?: string | null): Promise<Plan> =>
    post(`/plans/${id}/decide`, { status, note }),
  cancelPlan: (id: string): Promise<Plan> => post(`/plans/${id}/cancel`),

  connection: (weekKey?: string): Promise<ConnectionView> =>
    request(`/connection${weekKey ? `?weekKey=${weekKey}` : ''}`),
  logConnection: (input: {
    title: string
    ideaId?: string | null
    happenedOn: string
    participants: string[]
    note?: string | null
  }): Promise<{ ok: true; id: string }> => post('/connection', input),
  deleteConnection: (id: string): Promise<{ ok: true }> => del(`/connection/${id}`),


  members: (): Promise<Member[]> => request('/members'),
  createMember: (input: {
    name: string
    role: 'parent' | 'kid'
    emoji?: string
  }): Promise<Member> => post('/members', input),
  updateMember: (id: string, input: Partial<Member>): Promise<Member> => patch(`/members/${id}`, input),
  deleteMember: (id: string): Promise<{ ok: true }> => del(`/members/${id}`),
  /** Sets a kid's username, and their password when one is given (required for a first login). */
  setMemberLogin: (id: string, input: { username: string; password?: string }): Promise<Member> =>
    put(`/members/${id}/login`, input),
  removeMemberLogin: (id: string): Promise<Member> => del(`/members/${id}/login`),

  updateHousehold: (input: Partial<Household>): Promise<Household> => patch('/household', input),
  changePin: (currentPin: string, newPin: string): Promise<{ ok: true }> =>
    post('/household/pin', { currentPin, newPin }),
}
